import {Tooltip} from 'platform/components';
import {Text} from 'platform/foundation';
import styled from 'styled-components';

import {ReactNode} from 'react';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {MarketMetrics} from '../utils/computeMarketMetrics';
import {getPositionFillColor} from '../utils/getPositionFillColor';
import {MarketBars} from './MarketBars';
import {MetricsSkeleton} from './MetricsSkeleton';
import {PriceLadder} from './PriceLadder';

interface PresetMetricsCellsProps extends TestIdProps {
  name: string;
  nameBadge?: ReactNode;
  /** Rendered at the very start of the identity cell (drag handle in sortable rows). */
  leadingSlot?: ReactNode;
  description?: string | null;
  metrics: MarketMetrics | null;
  isLoading: boolean;
  formatPrice: (price: number | null) => string;
}

/**
 * The three metric cells of one saved filter: identity with my price position,
 * supply × demand bars, and the advertised price ladder. Rendered inside the shared
 * panel grid so cells align across the collapsed bar and expanded rows.
 * A filter matching no vehicles collapses into a single subdued empty message.
 */
export function PresetMetricsCells(props: PresetMetricsCellsProps) {
  const position = props.metrics?.position ?? null;
  const positionPercent = position?.percent ?? 0;

  const isEmptyMarket = !props.isLoading && props.metrics?.totalHits === 0;

  const identityHeader = (
    <>
      <NameRow>
        <Text size="small" alternative noWrap data-testid={suffixTestId('presetName', props)}>
          {props.name}
        </Text>
        {props.nameBadge}
      </NameRow>
      {isNotNilOrEmpty(props.description) ? (
        <Text size="xSmall" color="tertiary" noWrap>
          {props.description}
        </Text>
      ) : null}
    </>
  );

  if (isEmptyMarket) {
    return (
      <>
        <IdentityCell>
          {props.leadingSlot}
          <IdentityContent>{identityHeader}</IdentityContent>
        </IdentityCell>
        <EmptyMarketCell>
          <Text size="xSmall" color="tertiary" data-testid={suffixTestId('emptyMarket', props)}>
            {i18n.t('entity.filterForm.savedFiltersPanel.emptyMarket')}
          </Text>
        </EmptyMarketCell>
      </>
    );
  }

  return (
    <>
      <IdentityCell>
        {props.leadingSlot}
        <IdentityContent>
          {identityHeader}
          <Tooltip
            label={i18n.t('entity.filterForm.savedFiltersPanel.tooltip.position')}
            placement="top-start"
          >
            <PositionRow>
              <PositionLabel>
                <Text size="xSmall" color="secondary" noWrap>
                  {i18n.t('entity.filterForm.savedFiltersPanel.position')}
                </Text>
              </PositionLabel>
              <PositionTrack>
                <PositionFill $percentage={positionPercent} />
              </PositionTrack>
              {props.isLoading ? (
                <MetricsSkeleton width="64px" height={4} />
              ) : (
                // The numbers must never be clipped — only the track above shrinks
                <PositionValues>
                  <Text
                    size="xSmall"
                    alternative
                    noWrap
                    data-testid={suffixTestId('position', props)}
                  >
                    {isNotNil(position) ? `${position.place}/${position.total}` : '–'}
                  </Text>
                  {isNotNil(position) ? (
                    <Text size="xSmall" color="tertiary" noWrap>
                      {`(${position.percent} %)`}
                    </Text>
                  ) : null}
                </PositionValues>
              )}
            </PositionRow>
          </Tooltip>
        </IdentityContent>
      </IdentityCell>

      <MarketBars
        metrics={props.metrics}
        isLoading={props.isLoading}
        formatPrice={props.formatPrice}
        data-testid={props['data-testid']}
      />

      <LadderCell>
        <PriceLadder
          metrics={props.metrics}
          isLoading={props.isLoading}
          formatPrice={props.formatPrice}
          data-testid={props['data-testid']}
        />
      </LadderCell>
    </>
  );
}

/** Extra breathing room between the supply×demand section and the price ladder. */
const LadderCell = styled.div`
  padding-left: ${({theme}) => theme.getSize(6)};

  /* Stacked layout: full-width row, the step boxes share it equally (no scrolling) */
  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    grid-column: 1 / -1;
    padding-left: 0;
    min-width: 0;
  }
`;

const IdentityCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(2)};
  min-width: 0;
`;

const IdentityContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({theme}) => theme.getSize(1)};
  min-width: 0;
  flex: 1;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(1)};
  min-width: 0;
`;

/** Spans the supply×demand and ladder tracks when the filter matches no vehicles. */
const EmptyMarketCell = styled.div`
  grid-column: 2 / 4;
  display: flex;
  align-items: center;

  @media ${({theme}) => theme.devices.mobile}, ${({theme}) => theme.devices.tablet} {
    grid-column: 1 / -1;
  }
`;

const PositionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(2)};
  min-width: 0;
`;

/** Label truncates only as a last resort — after the track has fully collapsed. */
const PositionLabel = styled.div`
  flex: 0 1 auto;
  min-width: 0;
`;

/** The position values stay fully visible; the track above yields the space instead. */
const PositionValues = styled.div`
  display: flex;
  align-items: center;
  gap: ${({theme}) => theme.getSize(2)};
  flex-shrink: 0;
`;

const PositionTrack = styled.div`
  /* High shrink factor: on narrow panels the track collapses first, keeping the
     label whole and the numbers uncut. */
  flex: 0 20 ${({theme}) => theme.getSize(30)};
  min-width: ${({theme}) => theme.getSize(6)};
  height: ${({theme}) => theme.getSize(2)};
  border-radius: ${({theme}) => theme.radii.small};
  background-color: ${({theme}) => theme.colors.palettes.neutral[20][100]};
  overflow: hidden;
`;

const PositionFill = styled.div<{$percentage: number}>`
  height: 100%;
  border-radius: ${({theme}) => theme.radii.small};
  width: ${({$percentage}) => Math.max(0, Math.min(100, $percentage))}%;
  background-color: ${({theme, $percentage}) => getPositionFillColor(theme, $percentage)};
  transition: width 0.3s ease;
`;
